import type { Side } from 'driver.js';

/**
 * =============================================================================
 * HOME TOUR CONTENT — edit this file to change copy, order, or targets
 * =============================================================================
 * After internal testing, update text / button labels / step order here only.
 */

/** Button labels shown in the welcome modal and tour popovers. */
export const HOME_TOUR_BUTTONS = {
  next: 'Next',
  previous: 'Back',
  skip: 'Skip tour',
  finish: 'Finish',
  restart: 'Take a tour',
  start: 'Start tour',
};

/** Floating replay-tour control (bottom-right stack). */
export const HOME_TOUR_FAB = {
  tooltip: 'Take a tour',
  ariaLabel: 'Take a tour of Home',
};

/** Welcome modal (shown before the spotlight tour starts). */
export const HOME_TOUR_WELCOME = {
  title: 'Welcome to the Home page',
  student:
    'Home brings everything together in one place, helping you continue learning, keep up with important updates, and easily access the spaces and features to help you succeed.',
  educator:
    'Home brings everything together in one place, helping you continue teaching, keep up with important updates, and easily access the spaces and features to help you succeed.',
};

export type HomeTourStepId =
  | 'aula-logo'
  | 'role-toggle'
  | 'spaces'
  | 'space-progress'
  | 'space-engagement'
  | 'space-cta'
  | 'recent-updates'
  | 'recent-updates-reactions'
  | 'recent-updates-comments'
  | 'messages'
  | 'upcoming-assignments'
  | 'recently-viewed'
  | 'starred'
  | 'quick-links'
  | 'manage';

/**
 * Step order per role — reorder these arrays to change the tour sequence.
 * Both roles: Recent Updates comes after space steps, before Messages.
 */
export const HOME_TOUR_STUDENT_ORDER: HomeTourStepId[] = [
  'aula-logo',
  'role-toggle',
  'spaces',
  'space-progress',
  'space-cta',
  'recent-updates',
  'recent-updates-reactions',
  'recent-updates-comments',
  'messages',
  'upcoming-assignments',
  'recently-viewed',
  'starred',
  'quick-links',
  'manage',
];

export const HOME_TOUR_EDUCATOR_ORDER: HomeTourStepId[] = [
  'aula-logo',
  'role-toggle',
  'spaces',
  'space-engagement',
  'space-cta',
  'recent-updates',
  'recent-updates-reactions',
  'recent-updates-comments',
  'messages',
  'upcoming-assignments',
  'recently-viewed',
  'starred',
  'quick-links',
  'manage',
];

type HomeTourStepContent = {
  /** Spotlight title (shared). Use titleStudent / titleEducator when they differ. */
  title?: string;
  titleStudent?: string;
  titleEducator?: string;
  /** Shared description. Use descriptionStudent / descriptionEducator when they differ. */
  description?: string;
  descriptionStudent?: string;
  descriptionEducator?: string;
  /** CSS selector for the highlighted element */
  element: string;
  /** Popover side relative to the highlight */
  side?: Side;
  /** Popover align along that side */
  align?: 'start' | 'center' | 'end';
};

/**
 * All step titles, descriptions, selectors, and popover placement.
 * Change any string here — order is controlled by the arrays above.
 */
export const HOME_TOUR_STEPS: Record<HomeTourStepId, HomeTourStepContent> = {
  'aula-logo': {
    title: 'Aula logo',
    description: 'Select the Aula logo to return to Home.',
    element: '[data-tour="home-aula-logo"]',
    side: 'bottom',
  },

  'role-toggle': {
    title: 'Switch role',
    descriptionStudent:
      'If you have both student and educator roles, use this toggle to switch between your student and educator Home view.',
    descriptionEducator:
      'If you have both student and educator roles, use this toggle to switch between your student and educator Home view.',
    element: '#home-switchrole-widget',
    side: 'bottom',
  },

  spaces: {
    title: 'Your spaces',
    descriptionStudent:
      'Your learning, Course community or training spaces are all in one place. Select a space name to open it.',
    descriptionEducator:
      'Your teaching, Course community or training spaces are all in one place. Select a space name to open it.',
    element: '#home-space-widget',
    side: 'bottom',
  },

  'space-progress': {
    title: 'Track your progress',
    description:
      'Track your progress in each space and check back regularly for new Materials.',
    element: '[data-tour="home-space-card-progress"]',
    side: 'bottom',
  },

  'space-engagement': {
    title: 'Engagement analytics',
    description:
      'See how students are engaging with the content in each space.',
    element: '[data-tour="home-space-card-engagement"]',
    side: 'bottom',
  },

  'space-cta': {
    titleStudent: 'Start learning or Continue',
    titleEducator: 'Open Materials or Continue',
    descriptionStudent:
      'Continue from your last Materials page or start learning straight away, without searching through your spaces.',
    descriptionEducator:
      'Continue from your last Materials page or open Materials straight away, without searching through your spaces.',
    element: '[data-tour="home-space-card-cta"]',
    side: 'bottom',
  },

  'recent-updates': {
    title: 'Recent updates',
    descriptionStudent:
      'Stay up to date with the latest Announcements and Common room activity. React, comment, and filter the feed to view educator posts only. Green-blue posts are from educators, and purple posts are from students.',
    descriptionEducator:
      'Stay up to date with the latest Announcements and Common room activity. React, comment, and filter the feed to view educator posts only. Green-blue posts are from educators, and purple posts are from students.',
    element: '#home-recent-updates-widget',
    side: 'left',
    align: 'start',
  },

  'recent-updates-reactions': {
    title: 'View reactions',
    descriptionStudent:
      'Select the reaction count to view reactions and manage your own.',
    descriptionEducator:
      'Select the reaction count to view reactions and manage your own.',
    element: '[data-tour="home-recent-updates-card-cta"]',
    side: 'left',
    align: 'start',
  },

  'recent-updates-comments': {
    title: 'View comments',
    descriptionStudent:
      'Select the comment icon to read, post, and respond to comments.',
    descriptionEducator:
      'Select the comment icon to read, post, and respond to comments.',
    element: '[data-tour="home-recent-updates-card-comments"]',
    side: 'left',
    align: 'start',
  },

  messages: {
    title: 'Messages',
    description:
      'Catch up on your latest conversations and unread messages. Select any conversation to view and reply without leaving Home.',
    element: '#home-messages-widget',
    side: 'right',
    align: 'start',
  },

  'upcoming-assignments': {
    title: 'Upcoming assignments',
    descriptionStudent:
      'View your upcoming assignments, with those due soon shown first. Select any assignment to view the details or submit your work.',
    descriptionEducator:
      'View your upcoming assignments, with those due soon shown first. Select any assignment to view the assignment details and submissions.',
    element: '#home-upcoming-assignments-widget',
    side: 'right',
    align: 'start',
  },

  'recently-viewed': {
    title: 'Recently viewed spaces',
    description:
      "Quickly return to spaces you've visited recently. Selecting a space takes you straight back to where you left off, whether that's Materials, Announcements, or the Common room.",
    element: '#home-recently-viewed-spaces-widget',
    side: 'left',
    align: 'start',
  },

  starred: {
    title: 'Starred spaces',
    descriptionStudent:
      'Keep important spaces within easy reach. Star or unstar any space at any time.',
    descriptionEducator:
      'Keep important spaces within easy reach. Star or unstar any space at any time.',
    element: '#home-starred-spaces-widget',
    side: 'right',
    align: 'start',
  },

  'quick-links': {
    title: 'Quick links',
    descriptionStudent:
      'Access important institutional tools and learning resources directly from Home.',
    descriptionEducator:
      'Access important institutional tools and learning resources directly from Home.',
    element: '#home-quick-links-widget',
    side: 'right',
    align: 'start',
  },

  manage: {
    title: 'Personalise your Home page',
    description:
      'Hide or show widgets to organise Home in a way that works best for you.',
    element: '#home-manage-widget',
    side: 'left',
    align: 'start',
  },
};
